import DOMPurify from 'dompurify';
import _ from 'lodash';
import React, { Children, cloneElement, useEffect, useState } from 'react';
import { Key } from 'w3c-keys';
import { ALLOWED_CSS_PROPERTIES, ALLOWED_HTML_ATTRIBUTES, ALLOWED_HTML_TAGS } from './constants';

export function cloneChildren(children: React.ReactNode, props: any) {
  return Children.map(
    // remove null item
    Children.toArray(children).filter((child) => Boolean(child)),
    (item) => cloneElement(item as React.ReactElement<any>, props)
  );
}
export function getHashForString(str: string): number {
  let hash = 0;
  if (str) {
    Array.from(str).forEach((_letter, index) => {
      const charCode = str.charCodeAt(index);
      hash += charCode;
    });
  }
  return hash;
}
export function generateUUID() {
  /* eslint-disable no-bitwise, no-mixed-operators */
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  /* eslint-enable no-bitwise, no-mixed-operators */
}
export function parseDOMString(html: string, querySelector: string, text: string) {
  const domparser = new DOMParser();
  const doc = domparser.parseFromString(html, 'text/html');
  const elem = doc.querySelector(querySelector) as HTMLElement;
  elem.insertAdjacentText('beforeend', text);
  return doc.body.innerHTML;
}

/**
 * @returns new html by removing
 * element selected by queryselector
 */

export function removeElement(html: string, querySelector: string) {
  const domparser = new DOMParser();
  const doc = domparser.parseFromString(html, 'text/html');
  const elem = doc.querySelector(querySelector) as HTMLElement;
  elem.remove();
  return doc.body.innerHTML;
}

export const inIFrame = () => {
  return window.self !== window.top;
};

/**
 * @returns function
 * helper method to generate unique
 * key props for react component
 */
const generateUniqueKey = (): any => {
  let counter = 1;
  const map = new WeakMap<any, number>();
  const rduid = (item: any, index?: number): string => {
    if (typeof item === 'number' || typeof item === 'string') {
      return index ? `rd-idx-${index}` : `rd-val-${item}`;
    }
    if (!map.has(item)) {
      map.set(item, (counter += 1));
      return rduid(item);
    }
    return `rd-id-${map.get(item)}`;
  };
  return rduid;
};
export const getUniqueKey = generateUniqueKey();

/** React hooks to handle keys */
export const useKeyPressNavigation = (
  targetElement: any,
  items: any,
  itemSelectCallBack: any = _.noop,
  isVisible: boolean
): any => {
  const [cursor, setCursor] = useState(-1);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === Key.ArrowUp && isVisible) {
      event.preventDefault();
      if (cursor === -1) {
        setCursor(0);
      } else {
        setCursor((prevState) => (prevState > 0 ? prevState - 1 : items?.length - 1));
      }
    } else if (event.key === Key.ArrowDown && isVisible) {
      event.preventDefault();
      if (cursor === -1) {
        setCursor(0);
      } else {
        setCursor((prevState) => (prevState < items?.length - 1 ? prevState + 1 : 0));
      }
    } else if (event.key === Key.Escape || event.key === Key.Tab) {
      setCursor(-1);
    } else if (event.key === Key.Enter) {
      itemSelectCallBack(items[cursor]);
    }
  };

  const handleMouseDown = () => {
    setCursor(-1);
  };

  useEffect(() => {
    if (!isVisible) setCursor(-1);
  }, [isVisible]);

  useEffect(() => {
    if (targetElement) {
      targetElement.addEventListener('keydown', handleKeyDown);
      targetElement.addEventListener('mousedown', handleMouseDown);
    }

    return () => {
      if (targetElement) {
        targetElement.removeEventListener('keydown', handleKeyDown);
        targetElement.removeEventListener('mousedown', handleMouseDown);
      }
    };
  });

  return { cursor, setCursor };
};

/**
 * Without this hack, story shots (which we use for snapshot testing)
 * will fail if your story contains a React hook.
 *
 * How to use: Wrap your story which uses a hook inside this function.
 *
 * export const exampleStory = createStoryWithHooks(() => {
 *     // write the story which uses a hook here
 * });
 *
 * https://github.com/storybookjs/storybook/issues/8177
 */
export const createStoryWithHooks = (StoryFn: React.FC) => {
  const Component = () => <StoryFn />;
  Component.displayName = `StoryWithHooks(${StoryFn.displayName || StoryFn.name || 'Anonymous'})`;
  return Component;
};

/**
 * Given an object, return an array of keys that are not
 * extra properties added by react-docgen-typescript-loader
 *
 * @returns array of valid keys that are not added by docgen-loader
 */
export const getNonDocgenKeys = (enumObj: object) => {
  const blacklistedKeys = ['displayName', '__docgenInfo'];
  return Object.keys(enumObj).filter((key) => !blacklistedKeys.includes(key));
};

/**
 * Filter out extra properties added by react-docgen-typescript-loader
 *
 * @returns string array of enum values
 */
export const getEnumValues = (enumObj: object): string[] =>
  getNonDocgenKeys(enumObj).map((key) => enumObj[key]);

/**
 * Filter out extra properties added by react-docgen-typescript-loader
 * Useful for using enums as knobs. See icon.stories.tsx
 *
 * @returns enum object without docgen properties
 */
export const getEnumKnobs = (enumObj: object) =>
  getNonDocgenKeys(enumObj).reduce((obj, key) => ({ ...obj, [key]: enumObj[key] }), {});

// In case if you want to specify the type of component, use this utility type
// It is useful if you want to check the type a children of a specific component.
// example in textarea component.
export type RdComponentType<P> = React.FC<P> & {
  RD_COMPONENT: string;
};

export enum RD_COMPONENTS {
  TEXTAREA = 'TEXTAREA',
  INPUT_TEXT = 'INPUT_TEXT',
  INPUT_NUMBER = 'INPUT_NUMBER',
  DROPDOWN = 'DROPDOWN',
  INPUT_FILE = 'INPUT_FILE',
  MULTI_SELECT_DROPDOWN = 'MULTI_SELECT_DROPDOWN',
}

export enum ScreenSizeClass {
  HD,
  FHD,
  QHD,
  UHD,
}

const hdScalingClassName = 'rd-scaling-hd';
const fhdScalingClassName = 'rd-scaling-fhd';
const qhdScalingClassName = 'rd-scaling-qhd';
const uhdScalingClassName = 'rd-scaling-uhd';

export const SCALING_CLASS_NAMES = [
  hdScalingClassName,
  fhdScalingClassName,
  qhdScalingClassName,
  uhdScalingClassName,
];
interface ResponsiveBreakpoint {
  name: string;
  screenSizeClass: ScreenSizeClass;
  minWidth: number;
  maxWidth: number;
  className: string;
}

const ResponsiveBreakpoints: ResponsiveBreakpoint[] = [
  {
    name: 'HD',
    screenSizeClass: ScreenSizeClass.HD,
    minWidth: 0,
    maxWidth: 1600,
    className: hdScalingClassName,
  },
  {
    name: 'FHD',
    screenSizeClass: ScreenSizeClass.FHD,
    minWidth: 1600,
    maxWidth: 1920,
    className: fhdScalingClassName,
  },
  {
    name: 'QHD',
    screenSizeClass: ScreenSizeClass.QHD,
    minWidth: 1920,
    maxWidth: 2560,
    className: qhdScalingClassName,
  },
  {
    name: 'UHD',
    screenSizeClass: ScreenSizeClass.UHD,
    minWidth: 2560,
    maxWidth: Infinity,
    className: uhdScalingClassName,
  },
];

/**
 * Checks the screen size class from the current window width
 * @returns Responsive breakpoint object
 */
export const getResponsiveBreakpoint = () => {
  const screenWidth = inIFrame() ? window.innerWidth : window.outerWidth;
  const currentBreakpoint = ResponsiveBreakpoints.find((breakpoint) => {
    return screenWidth >= breakpoint.minWidth && screenWidth <= breakpoint.maxWidth;
  });
  return currentBreakpoint;
};

/**
 * Pass the onClick handler function as onEnter, onKeyDown is optional
 * Handles the elements being accessible using keyboard
 *
 * @returns the callback to be assigned to the onKeyDown event handler
 */
export const getOnEnter = (
  onEnter?: (evt: React.KeyboardEvent<HTMLElement>) => void,
  onKeyDown?: (evt: React.KeyboardEvent<HTMLElement>) => void
) => {
  return onKeyDown || onEnter
    ? (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === Key.Enter && onEnter) {
          onEnter(event);
        }
        if (onKeyDown) {
          onKeyDown(event);
        }
      }
    : _.noop;
};
export const copyTextToClipboard = (text: string, onCopy?: () => void) => {
  if (!_.isString(text)) {
    return;
  }
  let textArea: HTMLTextAreaElement | null = document.querySelector('#copy-text');
  if (!textArea) {
    textArea = document.createElement('textarea');
    textArea.id = 'copy-text';
    textArea.style.position = 'absolute';
    textArea.style.left = '-1000px';
    textArea.style.top = `${document.body.scrollTop}px`;
    document.body.appendChild(textArea);
  }
  textArea.value = text;
  textArea.select();
  textArea.focus();
  document.execCommand('copy');
  setTimeout(() => {
    if (textArea) {
      textArea.value = '';
    }
    if (onCopy) {
      onCopy();
    }
  }, 100);
};

/** Returns true if the browser used is Safari or else false */
export const isSafari = () => {
  // Get the user-agent string for checking the browser type
  const userAgentString = navigator.userAgent;
  // Detect Chrome Browser
  const isChromeBrowser = userAgentString.indexOf('Chrome') > -1;
  // Detect Safari Browser
  let isSafariBrowser = userAgentString.indexOf('Safari') > -1;

  // Discard Safari since it also matches Chrome
  if (isChromeBrowser && isSafariBrowser) {
    isSafariBrowser = false;
  }

  return isSafariBrowser;
};

/**
     * Return the top and left positions of the element relative to document.
     *
     * @param {HTMLDivElement} element - HTML Div element
     * @return {targetTop: number, targetLeft: number}

    */
export const getPositionInViewport = (element: HTMLElement): { top: number; left: number } => {
  const rect = element.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return {
    top: rect.top + scrollTop,
    left: rect.left + scrollLeft,
  };
};

// Enabling re-use of context object for better performance
let canvasContext;

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font
 * in pixels.
 * Prefer this function over getPixelWidthOfString
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with
 * (e.g. "bold 14px verdana").
 *
 * @see http://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export const getPixelWidthOfStringUsingCanvas = (text: string, font: string) => {
  canvasContext = document.createElement('canvas').getContext('2d');
  if (canvasContext) {
    canvasContext.font = font;
    const metrics = canvasContext.measureText(text);
    return metrics.width;
  }
  return 0;
};

/**
 *  Take CSS property-value pairs and validate against white-list,
 *  then add the styles to an array of property-value pairs and return it
 */
const validateStyles = (styles: CSSStyleDeclaration) => {
  const output: string[] = [];

  if (styles) {
    for (let i = 0; i < styles.length; i++) {
      const styleKey = styles[i];
      const normalizedKey = styleKey.replace(/([A-Z])/g, '-$1').toLowerCase();
      if (ALLOWED_CSS_PROPERTIES.includes(normalizedKey)) {
        const value = styles.getPropertyValue(styleKey);
        output.push(`${normalizedKey}: ${value};`);
      }
    }
  }

  return output;
};

// Add hooks to the custom DOMPurify object to remove disallowed CSS Properties
const addSanitizeCSSHooks = (customPurify: DOMPurify.DOMPurify) => {
  // Add a hook to enforce CSS attribute sanitization
  customPurify.addHook('afterSanitizeAttributes', (node: any) => {
    // Check all style attribute values and validate them
    if (node?.hasAttribute('style')) {
      const allowedStyles = validateStyles(node?.style);
      // re-add styles in case any are left
      if (allowedStyles?.length) {
        node.setAttribute('style', allowedStyles.join(''));
      } else {
        node.removeAttribute('style');
      }
    }
  });
};

/**
 * This function takes "dirty" HTML content as input and processes it to remove any potentially harmful
 * or unwanted elements, attributes, and styles, based on predefined allowed lists. It helps prevent
 * security vulnerabilities such as Cross-Site Scripting (XSS) by ensuring that only safe and allowed
 * HTML elements, attributes, and CSS properties are retained in the final output.
 * */
export const sanitizeHtml = (dirtyHtml: string): string => {
  const customPurify = DOMPurify();

  // Add hooks to process HTML content to remove disallowed CSS properties
  addSanitizeCSSHooks(customPurify);

  // Sanitize HTML content using DOMPurify
  const cleanHTML = customPurify.sanitize(dirtyHtml, {
    ALLOWED_TAGS: ALLOWED_HTML_TAGS,
    ALLOWED_ATTR: ALLOWED_HTML_ATTRIBUTES,
  });

  customPurify.removeAllHooks();

  return cleanHTML;
};

export enum SchedulerVariants {
  V1 = 'v1',
  V2 = 'v2',
}

export * from './testing';
